import { useState, useCallback } from "react";
import type { StatusCode } from "~/types/api";
import type { ListDTO } from "../../list.types";

type UseListSubmissionProps = {
  mode: "create" | "edit";
  list?: ListDTO;
  createList: (
    data: { name: string; workflowId: number },
    statuses?: StatusCode[]
  ) => Promise<unknown>;
  updateList: (
    listId: number,
    data: { name: string; workflowId: number; originalName?: string },
    statusCodes?: StatusCode[],
    originalStatusCodes?: StatusCode[]
  ) => Promise<void>;
  onSuccess?: () => void;
  onClose?: () => void;
};

export const useListSubmission = ({
  mode,
  list,
  createList,
  updateList,
  onSuccess,
  onClose,
}: UseListSubmissionProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = useCallback(
    async (
      formData: { name: string; workflowId: number },
      selectedStatuses: StatusCode[],
      originalStatuses: StatusCode[]
    ) => {
      if (!formData.name || !formData.workflowId) return;

      setIsSubmitting(true);
      try {
        if (mode === "create") {
          await createList(
            { name: formData.name, workflowId: formData.workflowId },
            selectedStatuses
          );
        } else if (mode === "edit" && list) {
          await updateList(
            list.id,
            {
              name: formData.name,
              workflowId: formData.workflowId,
              originalName: list.name,
            },
            selectedStatuses,
            originalStatuses
          );
        }
        onSuccess?.();
        onClose?.();
      } catch (error) {
        console.error(`Failed to ${mode} list:`, error);
      } finally {
        setIsSubmitting(false);
      }
    },
    [mode, list, createList, updateList, onSuccess, onClose]
  );

  return {
    isSubmitting,
    handleSubmit,
  };
};
